import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { useAuth } from '@/state/AuthContext';
import {
  addFavorite,
  listFavorites,
  removeFavoriteById,
  reorderFavorites,
  type Favorite,
  type FavoriteItemType,
} from './api';
import {
  FAVORITES_QUERY_KEY_ROOT,
  favoritesQueryKey,
} from './favoritesCache';

export {
  clearFavoritesCache,
  FAVORITES_QUERY_KEY_ROOT,
  favoritesQueryKey,
} from './favoritesCache';

/** Monotonic generation so out-of-order mutation settle cannot clobber newer UI. */
let favoritesMutationGeneration = 0;

export function __resetFavoritesMutationGenerationForTests() {
  favoritesMutationGeneration = 0;
}

export function useFavorites() {
  const { user } = useAuth();
  const organizationId = user?.organizationId ?? null;

  return useQuery<Favorite[]>({
    queryKey: favoritesQueryKey(organizationId),
    queryFn: () => listFavorites(),
    enabled: Boolean(user?.id && organizationId),
    staleTime: 30_000,
    // Never show another org's favorites while switching context.
    placeholderData: undefined,
  });
}

function invalidateFavorites(
  queryClient: ReturnType<typeof useQueryClient>,
  organizationId: string | null | undefined,
) {
  void queryClient.invalidateQueries({
    queryKey: favoritesQueryKey(organizationId),
  });
  // Also clear any legacy unscoped ['favorites'] keys from older hooks.
  void queryClient.invalidateQueries({ queryKey: [FAVORITES_QUERY_KEY_ROOT] });
}

/** Generic toasts — never interpolate cached item titles into error copy. */
const TOAST_ADD_FAIL = 'Could not add to favorites';
const TOAST_REMOVE_FAIL = 'Could not remove from favorites';
const TOAST_REORDER_FAIL = 'Could not reorder favorites';

export function useAddFavorite() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const organizationId = user?.organizationId ?? null;

  return useMutation({
    mutationFn: ({
      itemType,
      itemId,
    }: {
      itemType: FavoriteItemType;
      itemId: string;
    }) => addFavorite(itemType, itemId),
    onMutate: () => {
      const generation = ++favoritesMutationGeneration;
      return { generation };
    },
    onSuccess: (favorite, _vars, ctx) => {
      if (ctx && ctx.generation !== favoritesMutationGeneration) return;
      const key = favoritesQueryKey(organizationId);
      const previous = queryClient.getQueryData<Favorite[]>(key) ?? [];
      if (!previous.some((f) => f.favoriteId === favorite.favoriteId)) {
        queryClient.setQueryData<Favorite[]>(key, [...previous, favorite]);
      }
      invalidateFavorites(queryClient, organizationId);
    },
    onError: () => {
      toast.error(TOAST_ADD_FAIL);
    },
  });
}

export function useRemoveFavorite() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const organizationId = user?.organizationId ?? null;

  return useMutation({
    mutationFn: ({ favoriteId }: { favoriteId: string }) =>
      removeFavoriteById(favoriteId),
    onMutate: async ({ favoriteId }) => {
      const generation = ++favoritesMutationGeneration;
      const key = favoritesQueryKey(organizationId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<Favorite[]>(key);
      if (previous) {
        queryClient.setQueryData<Favorite[]>(
          key,
          previous.filter((f) => f.favoriteId !== favoriteId),
        );
      }
      return { previous, key, generation };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous && ctx.key && ctx.generation === favoritesMutationGeneration) {
        queryClient.setQueryData(ctx.key, ctx.previous);
      }
      toast.error(TOAST_REMOVE_FAIL);
    },
    onSettled: (_data, _err, _vars, ctx) => {
      if (ctx && ctx.generation !== favoritesMutationGeneration) return;
      invalidateFavorites(queryClient, organizationId);
    },
  });
}

/**
 * Transitional helper: remove by itemType+itemId after resolving the row from cache.
 * Prefer useRemoveFavorite({ favoriteId }) when the id is known.
 */
export function useRemoveFavoriteByItem() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const organizationId = user?.organizationId ?? null;

  return useMutation({
    mutationFn: async ({
      itemType,
      itemId,
    }: {
      itemType: FavoriteItemType;
      itemId: string;
    }) => {
      const key = favoritesQueryKey(organizationId);
      const list =
        queryClient.getQueryData<Favorite[]>(key) ?? (await listFavorites());
      const row = list.find(
        (f) => f.itemType === itemType && f.itemId === itemId,
      );
      if (!row) {
        throw new Error('Favorite not found');
      }
      await removeFavoriteById(row.favoriteId);
      return row;
    },
    onSuccess: () => {
      invalidateFavorites(queryClient, organizationId);
    },
    onError: () => {
      toast.error(TOAST_REMOVE_FAIL);
    },
  });
}

export function useReorderFavorites() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const organizationId = user?.organizationId ?? null;

  return useMutation({
    mutationFn: (favoriteIds: string[]) => reorderFavorites(favoriteIds),
    onMutate: async (favoriteIds) => {
      const generation = ++favoritesMutationGeneration;
      const key = favoritesQueryKey(organizationId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<Favorite[]>(key);
      if (previous) {
        const byId = new Map(previous.map((f) => [f.favoriteId, f]));
        const next = favoriteIds
          .map((id, index) => {
            const row = byId.get(id);
            if (!row) return null;
            return { ...row, displayOrder: (index + 1) * 1000 };
          })
          .filter((f): f is Favorite => f != null);
        queryClient.setQueryData<Favorite[]>(key, next);
      }
      return { previous, key, generation };
    },
    onError: (_err, _vars, ctx) => {
      // 409 and other failures: restore exact prior order when this mutation is still current.
      if (ctx?.previous && ctx.key && ctx.generation === favoritesMutationGeneration) {
        queryClient.setQueryData(ctx.key, ctx.previous);
      }
      toast.error(TOAST_REORDER_FAIL);
    },
    onSuccess: (favorites, _vars, ctx) => {
      if (ctx && ctx.generation !== favoritesMutationGeneration) return;
      const key = favoritesQueryKey(organizationId);
      queryClient.setQueryData<Favorite[]>(key, favorites);
    },
    onSettled: (_data, _err, _vars, ctx) => {
      if (ctx && ctx.generation !== favoritesMutationGeneration) return;
      invalidateFavorites(queryClient, organizationId);
    },
  });
}
